import { useTheme } from "@emotion/react";
import {
  AddCircleOutline,
  DeleteOutline,
  ExpandMore,
  InfoOutlined,
  PhoneOutlined,
  TextsmsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import api from "../../../../../../services/api";
import VirtualizedAutocomplete from "../../../../../../components/virtualization/virtualizedAutocomplete";
import useStyles from "./styles";

export default function ConfigOpenTicketCliente() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [loading, setLoading] = useState(false);
  const [paramsOpenTicketCliente, setParamsOpenTicketCliente] = useState([]);
  const [error, setError] = useState(false);
  const [tipoAtendimento, setTipoAtendimento] = useState([]);
  const [loadingParams, setLoadingParams] = useState(false);
  const [origemContato, setOrigemContato] = useState([]);
  const [userResponsaveis, setUserResponsaveis] = useState([]);
  const [etapasFluxoAtendimento, setEtapasFluxoAtendimento] = useState([]);
  const [statusAtendimento, setStatusAtendimento] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [configBackupChange, setConfigBackupChange] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleAddConfig = () => {
    setParamsOpenTicketCliente((prev) => [
      ...prev,
      {
        id: null,
        origemContatoId: null,
        tipoAtendimentoId: null,
        statusAtendimentoId: null,
        usuariosResponsaveis: [],
        idEtapaFluxoAtendimento: "",
        forms: [], // Inicializa com a propriedade de formulários vazia
      },
    ]);
  };

  const handleRemoveConfig = (indexToRemove) => {
    setParamsOpenTicketCliente((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleChangeField = (index, field, value) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      updatedList[index] = {
        ...updatedList[index],
        [field]: value,
      };
      return updatedList;
    });
  };

  // --- NOVAS FUNÇÕES PARA GERENCIAMENTO DO FORMULÁRIO DINÂMICO ---
  const handleAddFormField = (configIndex) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      const currentForms = updatedList[configIndex].forms || [];
      updatedList[configIndex] = {
        ...updatedList[configIndex],
        forms: [
          ...currentForms,
          { name: "", type: 0, description: "" }, // Tipo 0 (campo livre) por padrão
        ],
      };
      return updatedList;
    });
  };

  const handleRemoveFormField = (configIndex, formFieldIndex) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      updatedList[configIndex] = {
        ...updatedList[configIndex],
        forms: updatedList[configIndex].forms.filter(
          (_, i) => i !== formFieldIndex,
        ),
      };
      return updatedList;
    });
  };

  const handleChangeFormField = (
    configIndex,
    formFieldIndex,
    property,
    value,
  ) => {
    setParamsOpenTicketCliente((prev) => {
      const updatedList = [...prev];
      const updatedForms = [...updatedList[configIndex].forms];
      updatedForms[formFieldIndex] = {
        ...updatedForms[formFieldIndex],
        [property]: value,
      };
      updatedList[configIndex] = {
        ...updatedList[configIndex],
        forms: updatedForms,
      };
      return updatedList;
    });
  };
  // ---------------------------------------------------------------

  const createConfig = async () => {
    setError(false);

    const hasEmptyFields = paramsOpenTicketCliente.some((item) => {
      return (
        !item.tipoAtendimentoId ||
        !item.statusAtendimentoId ||
        !item.origemContatoId ||
        !item.usuariosResponsaveis ||
        item.usuariosResponsaveis.length === 0
      );
    });

    // Validação extra opcional: Se houver um campo de form adicionado, ele deve ter um nome
    const hasEmptyFormNames = paramsOpenTicketCliente.some((item) =>
      item.forms?.some((formField) => !formField.name.trim()),
    );

    if (
      hasEmptyFields ||
      paramsOpenTicketCliente.length === 0 ||
      hasEmptyFormNames
    ) {
      setError(true);
      setSnackbar({
        children: hasEmptyFormNames
          ? "Por favor, dê um nome para todos os campos de formulário criados."
          : "Por favor, preencha todos os campos obrigatórios de todas as configurações.",
        severity: "error",
      });
      return;
    }

    const tiposIds = paramsOpenTicketCliente.map(
      (item) => item.tipoAtendimentoId,
    );
    const hasDuplicate = tiposIds.some(
      (id, index) => tiposIds.indexOf(id) !== index,
    );

    if (hasDuplicate) {
      setSnackbar({
        children:
          "Não é permitido salvar mais de uma configuração com o mesmo Tipo de Atendimento.",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/interno/create/configTicket", {
        listaConfigs: paramsOpenTicketCliente,
        tipoConfig: 1,
      });

      setSnackbar({
        children:
          "Configurações de abertura de ticket atualizadas com sucesso!",
        severity: "success",
      });

      if (Array.isArray(response?.data)) {
        const formattedList = response?.data
          ?.map((item) => {
            if (!item.config?.paramsOpenTicketCliente) return null;
            return {
              ...item.config.paramsOpenTicketCliente,
              id: item.id,
              forms: item?.forms || [], // Garante o retorno do backend
            };
          })
          .filter(Boolean);
        setParamsOpenTicketCliente(formattedList);
      }

      setIsEdit(false);
    } catch (error) {
      console.error("Erro ao criar configuração do cliente:", error);
      setSnackbar({
        children: "Erro ao criar configuração de ticket!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getConfigTicketCliente = async () => {
    try {
      const response = await api.post("/interno/find/configTicket", {
        tipoConfig: 1,
      });

      if (Array.isArray(response?.data)) {
        const formattedList = response.data
          ?.map((item) => {
            if (!item.config?.paramsOpenTicketCliente) return null;
            return {
              ...item.config.paramsOpenTicketCliente,
              id: item.id,
              forms: item?.forms || [], // Busca os forms salvos
            };
          })
          ?.filter(Boolean);
        setParamsOpenTicketCliente(formattedList);
      } else if (response?.data?.config?.paramsOpenTicketCliente) {
        const singleConfig = response.data.config.paramsOpenTicketCliente;
        const singleForms = response.data.forms || [];

        const mappedSingle = {
          ...singleConfig,
          id: response.data.id,
          forms: singleForms || [],
        };
        setParamsOpenTicketCliente([mappedSingle]);
      }
    } catch (error) {
      console.error("Erro ao buscar configuração do cliente:", error);
      setSnackbar({
        children: "Erro ao buscar configuração de ticket!",
        severity: "error",
      });
    }
  };

  const getParamsEditTicket = async () => {
    setLoadingParams(true);
    try {
      const response = await api.get("/interno/find/paramsNewTicket");
      setUserResponsaveis(response?.data?.userResponsaveis || []);
      setTipoAtendimento(response?.data?.tipoAtendimento || []);
      setOrigemContato(response?.data?.origemContato || []);
      const statusAtendimentoFiltered =
        response?.data?.statusAtendimento?.filter((item) => item?.id !== 3) ||
        [];
      setStatusAtendimento(statusAtendimentoFiltered);
      setEtapasFluxoAtendimento(response?.data?.etapasFluxoAtendimento || []);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    } finally {
      setLoadingParams(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([getParamsEditTicket(), getConfigTicketCliente()]);
    setLoading(false);
  };

  const handleEdit = () => {
    setIsEdit(true);
    setConfigBackupChange(JSON.parse(JSON.stringify(paramsOpenTicketCliente)));
  };

  const handleCancel = () => {
    setIsEdit(false);
    setError(false);
    setParamsOpenTicketCliente(configBackupChange);
  };

  return (
    <>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneOutlined color="primary" />
              <Typography variant="h5" sx={{ ...styles.title }}>
                Configurações de abertura de ticket por clientes
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={styles.boxMainConfig}>
                {paramsOpenTicketCliente?.length > 0 ? (
                  paramsOpenTicketCliente?.map((configItem, index) => (
                    <Paper
                      key={index}
                      sx={styles.paperItemConfig}
                      variant="outlined"
                    >
                      <Box sx={styles.boxTitleConfig}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color="primary"
                        >
                          Configuração #{index + 1}
                        </Typography>
                        {isEdit && (
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveConfig(index)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        )}
                      </Box>

                      {/* 1. TIPO DE ATENDIMENTO */}
                      <Box sx={styles.containerInfoConfig}>
                        <VirtualizedAutocomplete
                          size="small"
                          options={tipoAtendimento}
                          value={
                            tipoAtendimento?.find(
                              (item) =>
                                item?.id === configItem?.tipoAtendimentoId,
                            ) || null
                          }
                          getOptionLabel={(option) =>
                            option ? `${option?.descricao || ""} ` : ""
                          }
                          disabled={loadingParams || !isEdit}
                          onChange={(event, value) =>
                            handleChangeField(
                              index,
                              "tipoAtendimentoId",
                              value?.id || null,
                            )
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          sx={styles.autocompleteInfoConfig}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="filled"
                              error={error && !configItem?.tipoAtendimentoId}
                              helperText={
                                error &&
                                !configItem?.tipoAtendimentoId &&
                                "Campo obrigatório"
                              }
                              label="Tipo de atendimento"
                            />
                          )}
                        />
                        <Paper variant="outlined" sx={styles.paperDescription}>
                          <Typography sx={styles.subtitle}>
                            Selecione qual tipo de atendimento deve ser
                            atribuída ao ticket aberto pelo cliente.
                          </Typography>
                        </Paper>
                      </Box>

                      {/* 2. STATUS DO ATENDIMENTO */}
                      <Box sx={styles.containerInfoConfig}>
                        <VirtualizedAutocomplete
                          size="small"
                          options={statusAtendimento}
                          value={
                            statusAtendimento?.find(
                              (item) =>
                                item?.id === configItem?.statusAtendimentoId,
                            ) || null
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          getOptionLabel={(option) =>
                            option ? `${option?.descricao || ""} ` : ""
                          }
                          disabled={loadingParams || !isEdit}
                          onChange={(event, value) =>
                            handleChangeField(
                              index,
                              "statusAtendimentoId",
                              value?.id || null,
                            )
                          }
                          sx={styles.autocompleteInfoConfig}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="filled"
                              error={error && !configItem?.statusAtendimentoId}
                              helperText={
                                error &&
                                !configItem?.statusAtendimentoId &&
                                "Campo obrigatório"
                              }
                              label="Status do atendimento"
                            />
                          )}
                        />
                        <Paper variant="outlined" sx={styles.paperDescription}>
                          <Typography sx={styles.subtitle}>
                            Selecione qual status que deve ser atribuída ao
                            ticket aberto pelo cliente.
                          </Typography>
                        </Paper>
                      </Box>

                      {/* 3. ORIGEM DO CONTATO */}
                      <Box sx={styles.containerInfoConfig}>
                        <VirtualizedAutocomplete
                          size="small"
                          options={origemContato}
                          value={
                            origemContato?.find(
                              (item) =>
                                item?.idOrigemContato ===
                                configItem?.origemContatoId,
                            ) || null
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.idOrigemContato === value.idOrigemContato
                          }
                          getOptionLabel={(option) =>
                            option ? `${option?.origemContato || ""} ` : ""
                          }
                          onChange={(event, value) =>
                            handleChangeField(
                              index,
                              "origemContatoId",
                              value?.idOrigemContato || null,
                            )
                          }
                          sx={styles.autocompleteInfoConfig}
                          disabled={loadingParams || !isEdit}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="filled"
                              error={error && !configItem?.origemContatoId}
                              helperText={
                                error &&
                                !configItem?.origemContatoId &&
                                "Campo obrigatório"
                              }
                              label="Origem do contato"
                            />
                          )}
                        />
                        <Paper variant="outlined" sx={styles.paperDescription}>
                          <Typography sx={styles.subtitle}>
                            Selecione qual origem de contato deve ser atribuída
                            ao ticket aberto pelo cliente.
                          </Typography>
                        </Paper>
                      </Box>

                      {/* 4. USUÁRIO RESPONSÁVEL */}
                      <Box sx={styles.containerInfoConfig}>
                        <VirtualizedAutocomplete
                          multiple
                          limitTags={1}
                          size="small"
                          options={userResponsaveis}
                          disabled={loadingParams || !isEdit}
                          value={configItem?.usuariosResponsaveis || []}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          getOptionLabel={(option) => option?.name || ""}
                          onChange={(event, value) =>
                            handleChangeField(
                              index,
                              "usuariosResponsaveis",
                              value || [],
                            )
                          }
                          sx={styles.autocompleteInfoConfig}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={
                                error &&
                                (!configItem?.usuariosResponsaveis ||
                                  configItem?.usuariosResponsaveis?.length ===
                                    0)
                              }
                              helperText={
                                error &&
                                (!configItem?.usuariosResponsaveis ||
                                  configItem?.usuariosResponsaveis?.length ===
                                    0) &&
                                "Campo obrigatório"
                              }
                              size="small"
                              variant="filled"
                              label="Usuário responsável"
                            />
                          )}
                        />
                        <Paper variant="outlined" sx={styles.paperDescription}>
                          <Typography sx={styles.subtitle}>
                            Selecione quais usuários responsáveis devem ser
                            atribuídos ao ticket aberto pelo cliente.
                          </Typography>
                        </Paper>
                      </Box>

                      {/* 5. ETAPA FLUXO ATENDIMENTO */}
                      <Box sx={styles.containerInfoConfig}>
                        <VirtualizedAutocomplete
                          size="small"
                          options={etapasFluxoAtendimento}
                          disabled={loadingParams || !isEdit}
                          value={
                            etapasFluxoAtendimento.find(
                              (item) =>
                                item.idEtapaFluxoAtendimento ===
                                configItem?.idEtapaFluxoAtendimento,
                            ) || null
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.idEtapaFluxoAtendimento ===
                            value.idEtapaFluxoAtendimento
                          }
                          getOptionLabel={(option) =>
                            option?.etapaFluxoAtendimento || ""
                          }
                          onChange={(event, value) =>
                            handleChangeField(
                              index,
                              "idEtapaFluxoAtendimento",
                              value?.idEtapaFluxoAtendimento || "",
                            )
                          }
                          sx={styles.autocompleteInfoConfig}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="filled"
                              label="Etapa fluxo atendimento"
                            />
                          )}
                        />
                        <Paper variant="outlined" sx={styles.paperDescription}>
                          <Typography sx={styles.subtitle}>
                            Selecione a etapa do fluxo de atendimento que o
                            ticket aberto pelo cliente deve iniciar.
                          </Typography>
                        </Paper>
                      </Box>

                      {/* --- NOVA SEÇÃO: CAMPOS ADICIONAIS DO FORMULÁRIO --- */}
                      <Paper variant="outlined" sx={styles.paperForm}>
                        <Box sx={styles.boxPaperForm}>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Campos Personalizados do Formulário
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Opcional: Crie formulário personalizado para o
                              cliente preencher na abertura do ticket
                            </Typography>
                          </Box>
                          {isEdit && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddCircleOutline />}
                              onClick={() => handleAddFormField(index)}
                            >
                              Adicionar Campo
                            </Button>
                          )}
                        </Box>

                        {configItem?.forms && configItem?.forms?.length > 0 ? (
                          <Box sx={styles.campoForms}>
                            {configItem?.forms?.map(
                              (formField, formFieldIndex) => (
                                <Box
                                  key={formFieldIndex}
                                  sx={styles.boxTextfieldForm}
                                >
                                  <Box sx={styles.boxFormFieldHeader}>
                                    <Typography sx={styles.formFieldIndexLabel}>
                                      Campo {formFieldIndex + 1}
                                    </Typography>
                                  </Box>
                                  <Box sx={styles.formFieldTopRow}>
                                    <TextField
                                      size="small"
                                      label="Nome do campo (Ex: Responsável)"
                                      variant="filled"
                                      value={formField.name}
                                      disabled={!isEdit}
                                      error={error && !formField.name.trim()}
                                      onChange={(e) =>
                                        handleChangeFormField(
                                          index,
                                          formFieldIndex,
                                          "name",
                                          e.target.value,
                                        )
                                      }
                                      sx={styles.formFieldName}
                                    />

                                    <FormControl
                                      variant="filled"
                                      size="small"
                                      sx={styles.formFieldType}
                                    >
                                      <InputLabel
                                        id={`select-type-label-${index}-${formFieldIndex}`}
                                      >
                                        Tipo
                                      </InputLabel>
                                      <Select
                                        size="small"
                                        labelId={`select-type-label-${index}-${formFieldIndex}`}
                                        label="Tipo"
                                        value={formField.type}
                                        disabled={!isEdit}
                                        onChange={(e) =>
                                          handleChangeFormField(
                                            index,
                                            formFieldIndex,
                                            "type",
                                            Number(e.target.value),
                                          )
                                        }
                                        renderValue={(selected) => (
                                          <Box sx={styles.boxIconSelect}>
                                            {selected === 0 ? (
                                              <TextsmsOutlined
                                                fontSize="small"
                                                color="action"
                                              />
                                            ) : (
                                              <PhoneOutlined
                                                fontSize="small"
                                                color="action"
                                              />
                                            )}
                                            <Typography variant="body2">
                                              {selected === 0
                                                ? "Campo Livre"
                                                : "Telefone"}
                                            </Typography>
                                          </Box>
                                        )}
                                      >
                                        <MenuItem value={0}>
                                          <Box sx={styles.boxIconForm}>
                                            <TextsmsOutlined
                                              fontSize="small"
                                              color="action"
                                            />
                                            Campo Livre
                                          </Box>
                                        </MenuItem>
                                        <MenuItem value={1}>
                                          <Box sx={styles.boxIconForm}>
                                            <PhoneOutlined
                                              fontSize="small"
                                              color="action"
                                            />
                                            Telefone
                                          </Box>
                                        </MenuItem>
                                      </Select>
                                    </FormControl>

                                    {isEdit && (
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() =>
                                          handleRemoveFormField(
                                            index,
                                            formFieldIndex,
                                          )
                                        }
                                      >
                                        <DeleteOutline />
                                      </IconButton>
                                    )}
                                  </Box>

                                  {(isEdit || formField.description) && (
                                    <TextField
                                      size="small"
                                      variant="filled"
                                      label="Descrição auxiliar (opcional)"
                                      placeholder="Instruções de como preencher o campo"
                                      value={formField.description || ""}
                                      disabled={!isEdit}
                                      multiline
                                      maxRows={2}
                                      fullWidth
                                      onChange={(e) =>
                                        handleChangeFormField(
                                          index,
                                          formFieldIndex,
                                          "description",
                                          e.target.value,
                                        )
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <InfoOutlined
                                            fontSize="small"
                                            color="action"
                                            sx={styles.formFieldDescriptionIcon}
                                          />
                                        ),
                                      }}
                                      sx={styles.formFieldDescription}
                                    />
                                  )}
                                </Box>
                              ),
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              fontStyle: "italic",
                              textAlign: "center",
                              py: 1,
                            }}
                          >
                            Nenhum campo personalizado adicionado a esta
                            configuração.
                          </Typography>
                        )}
                      </Paper>
                      {/* -------------------------------------------------- */}
                    </Paper>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 100,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontStyle: "italic" }}
                    >
                      SEM CONFIGURAÇÃO
                    </Typography>
                  </Box>
                )}

                {isEdit && (
                  <Button
                    variant="dashed"
                    onClick={handleAddConfig}
                    startIcon={<AddCircleOutline />}
                    sx={{
                      mt: 2,
                      border: "1px dashed",
                      color: theme.palette.primary.main,
                    }}
                  >
                    Adicionar Nova Configuração
                  </Button>
                )}
              </Box>
            )}
          </Paper>

          {!loading && (
            <Box sx={styles.boxActions}>
              {!isEdit ? (
                <Button variant="contained" size="small" onClick={handleEdit}>
                  Editar
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    onClick={handleCancel}
                    size="small"
                    variant="outlined"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={createConfig}
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
